import { useState, memo } from "react";
import { ProSidebar, Menu, MenuItem } from "react-pro-sidebar";
import { Box, IconButton, Typography, useTheme } from "@mui/material";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "react-pro-sidebar/dist/css/styles.css";
import { tokens } from "../../theme";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
import ContactsOutlinedIcon from "@mui/icons-material/ContactsOutlined";
import ReceiptOutlinedIcon from "@mui/icons-material/ReceiptOutlined";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import PieChartOutlineOutlinedIcon from "@mui/icons-material/PieChartOutlineOutlined";
import TimelineOutlinedIcon from "@mui/icons-material/TimelineOutlined";
import MenuOutlinedIcon from "@mui/icons-material/MenuOutlined";
import MapOutlinedIcon from "@mui/icons-material/MapOutlined";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import EditNoteIcon from "@mui/icons-material/EditNote";
import LocalActivityIcon from "@mui/icons-material/LocalActivity";
import GroupsIcon from "@mui/icons-material/Groups";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import RateReviewOutlinedIcon from "@mui/icons-material/RateReviewOutlined";
import { authService } from "../../components/auth";

const Item = memo(({ title, to, icon, selected, setSelected, colors }) => {
  return (
    <MenuItem
      active={selected === title}
      style={{ color: colors.grey[100] }}
      onClick={() => setSelected(title)}
      icon={icon}
    >
      <Link to={to} style={{ textDecoration: 'none', color: 'inherit', width: '100%', display: 'block' }}>
        <Typography>{title}</Typography>
      </Link>
    </MenuItem>
  );
});

Item.displayName = "SidebarItem";

const Sidebar = memo(() => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { t } = useTranslation();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [selected, setSelected] = useState("Dashboard");

  // Role-based access control
  const isAdmin = authService.isAdmin();
  const canManageUsers = authService.canManageUsers(); // admin or manager
  const userName = authService.getUserName() || 'User';
  const userRole = authService.getUserRole() || 'user';

  // Format role for display
  const roleDisplay = userRole.charAt(0).toUpperCase() + userRole.slice(1);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        "& .pro-sidebar-inner": {
          background: `${colors.primary[400]} !important`,
          minHeight: "100vh",
        },
        "& .pro-icon-wrapper": {
          backgroundColor: "transparent !important",
        },
        "& .pro-inner-item": {
          padding: "5px 35px 5px 20px !important",
        },
        "& .pro-inner-item:hover": {
          color: "#868dfb !important",
        },
        "& .pro-menu-item.active": {
          color: "#6870fa !important",
        },
        "& .pro-sidebar": {
          minHeight: "100vh",
        },
      }}
    >
      <ProSidebar collapsed={isCollapsed}>
        <Menu iconShape="square">
          {/* LOGO AND MENU ICON */}
          <MenuItem
            onClick={() => setIsCollapsed(!isCollapsed)}
            icon={isCollapsed ? <MenuOutlinedIcon /> : undefined}
            style={{
              margin: "10px 0 20px 0",
              color: colors.grey[100],
            }}
          >
            {!isCollapsed && (
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                ml="15px"
              >
                <Box display="flex" alignItems="center" gap="10px">
                  <img
                    alt="logo"
                    src="/assets/logo.png"
                    style={{ width: "28px", height: "28px" }}
                  />
                  <Typography variant="h3" color={colors.grey[100]}>
                    VINLOT
                  </Typography>
                </Box>
                <IconButton onClick={() => setIsCollapsed(!isCollapsed)}>
                  <MenuOutlinedIcon />
                </IconButton>
              </Box>
            )}
          </MenuItem>

          {!isCollapsed && (
            <Box mb="25px">
              <Box display="flex" justifyContent="center" alignItems="center">
                <img
                  alt="profile-user"
                  width="100px"
                  height="100px"
                  src={`../../assets/user.png`}
                  style={{ cursor: "pointer", borderRadius: "50%" }}
                />
              </Box>
              <Box textAlign="center">
                <Typography
                  variant="h2"
                  color={colors.grey[100]}
                  fontWeight="bold"
                  sx={{ m: "10px 0 0 0" }}
                >
                  {userName}
                </Typography>
                <Typography variant="h5" color={colors.greenAccent[500]}>
                  {roleDisplay}
                </Typography>
              </Box>
            </Box>
          )}

          <Box paddingLeft={isCollapsed ? undefined : "10%"}>
            <Item
              title={t('nav.dashboard')}
              to="/dashboard"
              icon={<HomeOutlinedIcon />}
              selected={selected}
              setSelected={setSelected}
              colors={colors}
            />

            <Typography
              variant="h6"
              color={colors.grey[300]}
              sx={{ m: "15px 0 5px 20px" }}
            >
              Data
            </Typography>

            {/* Team - visible to all */}
            <Item
              title={t('nav.manageTeam')}
              to="/team"
              icon={<PeopleOutlinedIcon />}
              selected={selected}
              setSelected={setSelected}
              colors={colors}
            />

            {/* Contacts - only admin and manager */}
            {canManageUsers && (
              <Item
                title={t('nav.contactsInfo')}
                to="/contacts"
                icon={<ContactsOutlinedIcon />}
                selected={selected}
                setSelected={setSelected}
                colors={colors}
              />
            )}

            {/* Groups - only admin */}
            {isAdmin && (
              <Item
                title={t('nav.groups')}
                to="/groups"
                icon={<GroupsIcon />}
                selected={selected}
                setSelected={setSelected}
                colors={colors}
              />
            )}

            {/* Sales - visible to all */}
            <Item
              title={t('nav.salesObtained')}
              to="/sales-obtained"
              icon={<ReceiptOutlinedIcon />}
              selected={selected}
              setSelected={setSelected}
              colors={colors}
            />
            <Item
              title={t('nav.userSales')}
              to="/user-sales"
              icon={<LocalActivityIcon />}
              selected={selected}
              setSelected={setSelected}
              colors={colors}
            />
            <Item
              title={t('nav.lotteryResults')}
              to="/lottery-results"
              icon={<EmojiEventsIcon />}
              selected={selected}
              setSelected={setSelected}
              colors={colors}
            />
            {isAdmin && (
              <Item
                title={t('nav.manualDraw')}
                to="/manual-draw"
                icon={<EditNoteIcon />}
                selected={selected}
                setSelected={setSelected}
                colors={colors}
              />
            )}
            {isAdmin && (
              <Item
                title={t('nav.numberVolume')}
                to="/number-volume"
                icon={<TrendingUpIcon />}
                selected={selected}
                setSelected={setSelected}
                colors={colors}
              />
            )}
            {isAdmin && (
              <Item
                title="WA Import"
                to="/whatsapp-import"
                icon={<WhatsAppIcon />}
                selected={selected}
                setSelected={setSelected}
                colors={colors}
              />
            )}
            {isAdmin && (
              <Item
                title="Training Examples"
                to="/training-examples"
                icon={<SchoolOutlinedIcon />}
                selected={selected}
                setSelected={setSelected}
                colors={colors}
              />
            )}
            {isAdmin && (
              <Item
                title="Photo Review"
                to="/photo-review"
                icon={<RateReviewOutlinedIcon />}
                selected={selected}
                setSelected={setSelected}
                colors={colors}
              />
            )}

            <Typography
              variant="h6"
              color={colors.grey[300]}
              sx={{ m: "15px 0 5px 20px" }}
            >
              Pages
            </Typography>

            {/* Profile Form (Create User) - only admin and manager */}
            {canManageUsers && (
              <Item
                title={t('nav.profileForm')}
                to="/form"
                icon={<PersonOutlinedIcon />}
                selected={selected}
                setSelected={setSelected}
                colors={colors}
              />
            )}

            <Item
              title={t('nav.calendar')}
              to="/calendar"
              icon={<CalendarTodayOutlinedIcon />}
              selected={selected}
              setSelected={setSelected}
              colors={colors}
            />
            <Item
              title={t('nav.faq')}
              to="/faq"
              icon={<HelpOutlineOutlinedIcon />}
              selected={selected}
              setSelected={setSelected}
              colors={colors}
            />

            <Typography
              variant="h6"
              color={colors.grey[300]}
              sx={{ m: "15px 0 5px 20px" }}
            >
              Charts
            </Typography>
            <Item
              title={t('nav.barChart')}
              to="/bar"
              icon={<BarChartOutlinedIcon />}
              selected={selected}
              setSelected={setSelected}
              colors={colors}
            />
            <Item
              title={t('nav.pieChart')}
              to="/pie"
              icon={<PieChartOutlineOutlinedIcon />}
              selected={selected}
              setSelected={setSelected}
              colors={colors}
            />
            <Item
              title={t('nav.lineChart')}
              to="/line"
              icon={<TimelineOutlinedIcon />}
              selected={selected}
              setSelected={setSelected}
              colors={colors}
            />
            <Item
              title={t('nav.geographyChart')}
              to="/geography"
              icon={<MapOutlinedIcon />}
              selected={selected}
              setSelected={setSelected}
              colors={colors}
            />
          </Box>
        </Menu>
      </ProSidebar>
    </Box>
  );
});

Sidebar.displayName = "Sidebar";

export default Sidebar;
